import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/contexts/AuthContext";
import { useSiteSafe } from "@/lib/contexts/SiteContext";
import { useWorkspaceSafe } from "@/lib/contexts/WorkspaceContext";
import {
  getExperimentsList,
  getExperimentMetricsById,
  getExperimentGoalMetrics,
  createExperiment,
  stopExperiment,
  archiveExperiment,
  getExperimentPages,
  // AB Test Draft APIs
  getAbTestDraft,
  updateAbTestDraft,
  // AI APIs
  generateVariation,
  // Types
  type ComponentInstruction,
  type AbTestDraft,
  resumeExperiment,
  recordExperimentDecision,
  promoteExperimentVariant,
  putExperimentResultsGoal,
  type ExperimentDecisionPayload,
} from "@/lib/api/api";
import {
  logExperimentEvent,
  logExperimentListDegraded,
  logExperimentGoalMetricsUnavailable,
  logResultsGoalChanged,
  logResultsGoalConflict,
  reportExperimentFailure,
} from "@/lib/observability/experiments";
import { isCountGoal, twoProportionZTest } from "@/lib/utils/abTestStatistics";
import type { ResultsGoal } from "@/lib/experiments/goalMetric";
import { getHttpStatus } from "@/lib/observability/shared";
import { appendExperimentLog, type ExperimentLogEntry } from "@/lib/utils/experimentLog";
import { normalizeTestGoal } from "@/lib/utils/testGoal";
import type {
  GeoRestriction,
  AudienceRestriction,
} from "@/lib/types/experiment-restrictions";

// Types
export interface ExperimentVariant {
  id: string;
  url: string;
  label: string;
  notes: string;
  isControl: boolean;
  screenshots?: string[]; // Array of screenshot URLs
  instructions?: ComponentInstruction[]; // For component tests
  sessions?: number;
  visits?: number;
  conversions?: number;
  conversionRate?: number;
  bounceRate?: number;
  avgTimeOnPage?: number;
  /** Average scroll depth per view, 0-100. Null when the API sends none. */
  scrollDepth?: number | null;
  /** Form submits on the page, counted for every goal. */
  formSubmissions?: number;
  totalRevenue?: number;
  averageOrderValue?: number;
  revenuePerVisitor?: number;
  trialConversions?: number;
  paymentConversions?: number;
  trialConversionRate?: number;
  /** Total Views (page views), null when the API sends none. */
  totalViews?: number | null;
  /** Every click on the page; null when the launch goal is a form. */
  totalClicks?: number | null;
}

export interface Experiment {
  experimentId: string;
  experimentName: string;
  testGoal: string;
  status: "active" | "paused" | "completed" | "draft" | "archived";
  createdAt: string;
  endedAt?: string;
  updatedAt?: string;
  variants: ExperimentVariant[];
  isUserLevel?: boolean;
  geoRestriction?: GeoRestriction;
  audienceRestriction?: AudienceRestriction;
  finishPageUrl?: string;
  testType?: string;
  totalSessions?: number;
  totalConversions?: number;
  winningVariant?: string;
  statisticalSignificance?: number;
  plannedEndDate?: string;
  autoStopDurationDays?: number | null;
  stoppedReason?: "manual" | "auto";
  // Property info (populated in workspace aggregate mode)
  propertyId?: string;
  propertyName?: string;
  propertyDomain?: string;
  pinnedEvents?: Array<{ target: string; secondary: string }>;
  /** Goal the results are read on when changed after launch (raw; parse with parseResultsGoal). */
  resultsGoal?: unknown;
  resultsGoalHistory?: unknown;
  customDistribution?: number[];
}

interface ExperimentMetrics {
  experimentId: string;
  status: string;
  variants: Record<string, ExperimentVariant>;
  timeSeries?: Array<{
    date: string;
    [variantId: string]: any;
  }>;
  summary?: {
    totalSessions: number;
    totalConversions: number;
    overallConversionRate: number;
    statisticalSignificance: number;
    confidenceLevel: number;
    winningVariant?: string;
    improvement?: number;
  };
}

interface ExperimentsData {
  experiments: Experiment[];
}

// Helper to get propertyId from context
function usePropertyId(optionsPropertyId?: string) {
  const siteContext = useSiteSafe();
  const workspaceContext = useWorkspaceSafe();

  return (
    optionsPropertyId ||
    (workspaceContext ? workspaceContext.selectedPropertyId : null) ||
    siteContext?.currentSite?.propertyId ||
    siteContext?.id
  );
}

/**
 * Hook to fetch all experiments for a property (or all properties in workspace aggregate mode)
 */
export function useExperiments(options?: { propertyId?: string; token?: string }) {
  const { token: authToken } = useAuth();
  const token = options?.token || authToken;
  const workspaceContext = useWorkspaceSafe();
  const propertyId = usePropertyId(options?.propertyId);
  
  // Aggregate mode: more than one site selected in the URL
  const isAggregateMode = !!workspaceContext?.isAggregateMode;
  const aggregatePropertyIds = workspaceContext?.aggregatePropertyIds || [];
  const enabledProperties = workspaceContext?.enabledProperties || [];

  return useQuery<Experiment[]>({
    queryKey: ["experiments", isAggregateMode ? aggregatePropertyIds.join(',') : propertyId],
    queryFn: async (): Promise<Experiment[]> => {
      if (!token) throw new Error("Missing auth");

      // Fetch experiments - either from all selected properties or single property
      let response: any[];
      if (isAggregateMode) {
        const selectedProps = aggregatePropertyIds
          .map(id => enabledProperties.find(p => p.propertyId === id))
          .filter(Boolean) as typeof enabledProperties;
        const allExperiments = await Promise.all(
          selectedProps.map(async (prop) => {
            try {
              const propResponse = await getExperimentsList(prop.propertyId, token);
              return Array.isArray(propResponse)
                ? propResponse.map((exp: any) => ({
                    ...exp,
                    propertyId: prop.propertyId,
                    propertyName: prop.name,
                    propertyDomain: prop.domain,
                  }))
                : [];
            } catch (error) {
              logExperimentListDegraded(
                { propertyId: prop.propertyId, domain: prop.domain },
                error,
              );
              return [];
            }
          })
        );
        response = allExperiments.flat();
      } else {
        // Single property mode
        if (!propertyId) throw new Error("Missing propertyId");
        response = await getExperimentsList(propertyId, token);
      }

      // Transform backend response to frontend format
      // Backend returns: { experimentId, experiment: { experimentName, variants, ... }, status, createdAt, ... }
      // Frontend expects: { experimentId, experimentName, variants, status, createdAt, ... }
      if (Array.isArray(response)) {
        return response.map((exp) => {
          // Normalize variants - ensure we always get an array regardless of backend shape
          const rawVariants = exp.experiment?.variants;
          const backendVariants: any[] = Array.isArray(rawVariants)
            ? rawVariants
            : rawVariants && typeof rawVariants === 'object'
            ? Object.values(rawVariants)
            : [];
          const normalizedVariants = backendVariants.map(
            (variant: any, index: number) => {
              // Check if this is a traditional test (has id, label, etc) or component test (has instructions)
              const isComponentTest =
                exp.experimentType?.startsWith("component_");

              if (isComponentTest) {
                // Component test variant - add missing fields
                return {
                  id: variant.id || `variant-${index}`,
                  url: variant.url || "/",
                  label:
                    variant.label ||
                    (index === 0
                      ? "Control"
                      : `Variant ${String.fromCharCode(65 + index - 1)}`),
                  notes: variant.notes || "",
                  isControl: index === 0,
                  screenshots: variant.screenshots || [],
                  instructions: variant.instructions || [],
                };
              } else {
                // Traditional test - ensure all required fields exist
                return {
                  id: variant.id || `variant-${index}`,
                  url: variant.url || "/",
                  label:
                    variant.label ||
                    (variant.isControl
                      ? "Control"
                      : `Variant ${String.fromCharCode(65 + index)}`),
                  notes: variant.notes || "",
                  isControl: variant.isControl ?? index === 0,
                  screenshots: variant.screenshots || [],
                };
              }
            }
          );

          // Merge stats from transformed metrics if available
          const variantsWithStats = normalizedVariants.map(
            (v: {
              id: string;
              url: string;
              label: string;
              notes: string;
              isControl: boolean;
              screenshots: string[];
              instructions?: string[];
            }) => {
              const stats = exp.variants?.[v.id] || {};
              return {
                ...v,
                ...stats,
              };
            }
          );

          return {
            experimentId: exp.experimentId,
            experimentName: exp.experiment?.experimentName || "",
            testGoal: normalizeTestGoal(exp.experiment?.testGoal),
            status:
              exp.status === "active"
                ? "active"
                : exp.status === "complete"
                ? "completed"
                : exp.status === "archived"
                ? "archived"
                : exp.status === "draft"
                ? "draft"
                : "paused",
            createdAt: exp.createdAt,
            endedAt: exp.endedAt,
            updatedAt: exp.updatedAt,
            variants: variantsWithStats,
            isUserLevel: exp.experiment?.isUserLevel,
            customDistribution: exp.experiment?.customDistribution,
            geoRestriction: exp.experiment?.geoRestriction,
            audienceRestriction: exp.experiment?.audienceRestriction,
            finishPageUrl: exp.experiment?.finishPageUrl,
            testType: exp.experimentType,
            resultsGoal: exp.resultsGoal ?? exp.experiment?.resultsGoal,
            resultsGoalHistory: exp.resultsGoalHistory ?? exp.experiment?.resultsGoalHistory,
            // Include property info if in aggregate mode
            propertyId: exp._propertyId,
            propertyName: exp._propertyName,
            propertyDomain: exp._propertyDomain,
          };
        });
      }
      return [];
    },
    enabled: !!token && (isAggregateMode || !!propertyId),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to fetch detailed metrics for a specific experiment
 */
export function useExperimentMetrics(
  experimentId: string | null,
  options?: {
    propertyId?: string;
    includeTimeSeries?: boolean;
    token?: string;
    /** Poll while the test is live. Pass true for active experiments. */
    live?: boolean;
  }
) {
  const { token: authToken } = useAuth();
  const token = options?.token || authToken;
  const propertyId = usePropertyId(options?.propertyId);
  const includeTimeSeries = options?.includeTimeSeries ?? false;
  const live = options?.live ?? false;

  return useQuery({
    queryKey: [
      "experiment-metrics",
      propertyId,
      experimentId,
      includeTimeSeries,
    ],
    queryFn: async () => {
      if (!token || !propertyId || !experimentId)
        throw new Error("Missing required params");
      return getExperimentMetricsById(
        propertyId,
        experimentId,
        token,
        includeTimeSeries
      );
    },
    enabled: !!token && !!propertyId && !!experimentId,
    staleTime: 30 * 1000,
    // Live tests refresh every 30s so the verdict card stays current.
    refetchInterval: live ? 30 * 1000 : false,
    refetchIntervalInBackground: false,
  });
}

/**
 * Secondary request for the results page's "Measure by" goals
 * (`goals=all[&destination=]`). Separate from `useExperimentMetrics` on
 * purpose: one failed extra query makes the backend answer 500, and the
 * launch-goal view must never depend on it. A failure leaves `data` empty and
 * the page falls back to today's fields.
 */
export function useExperimentGoalMetrics(
  experimentId: string | null,
  options?: {
    propertyId?: string;
    token?: string;
    destination?: string | null;
    /** JSON click set for a live preview (editor draft). */
    clickSet?: string | null;
    live?: boolean;
    enabled?: boolean;
  }
) {
  const { token: authToken } = useAuth();
  const token = options?.token || authToken;
  const propertyId = usePropertyId(options?.propertyId);
  const destination = options?.destination?.trim() || null;
  const clickSet = options?.clickSet || null;

  return useQuery({
    queryKey: ["experiment-goal-metrics", propertyId, experimentId, destination, clickSet],
    queryFn: async () => {
      if (!token || !propertyId || !experimentId) throw new Error("Missing required params");
      try {
        return await getExperimentGoalMetrics(propertyId, experimentId, token, destination, clickSet);
      } catch (error) {
        logExperimentGoalMetricsUnavailable({ propertyId }, { experimentId, error });
        throw error;
      }
    },
    enabled: (options?.enabled ?? true) && !!token && !!propertyId && !!experimentId,
    staleTime: 30 * 1000,
    retry: 1,
    refetchInterval: options?.live ? 30 * 1000 : false,
    refetchIntervalInBackground: false,
    // A new destination path refetches; keep this experiment's last numbers on
    // screen meanwhile (never another experiment's).
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === experimentId ? previous : undefined,
  });
}

/**
 * Save (or reset, with `null`) the goal a test's results are read on. A 409
 * (someone else changed it) is expected friction, a warn Log; other failures
 * are Issues. The caller only toasts.
 */
export function useSaveResultsGoal(options?: { propertyId?: string; token?: string }) {
  const { token: authToken } = useAuth();
  const token = options?.token || authToken;
  const propertyId = usePropertyId(options?.propertyId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ experimentId, resultsGoal }: { experimentId: string; resultsGoal: ResultsGoal | null }) => {
      if (!token || !propertyId) throw new Error("Missing required params");
      return putExperimentResultsGoal(propertyId, experimentId, resultsGoal, token);
    },
    onSuccess: (result, { experimentId, resultsGoal }) => {
      // Show the saved goal at once; the refetch below confirms it.
      queryClient.setQueriesData<Experiment[]>({ queryKey: ["experiments", propertyId] }, (prev) =>
        Array.isArray(prev)
          ? prev.map((e) =>
              e.experimentId === experimentId
                ? { ...e, resultsGoal: result.resultsGoal, resultsGoalHistory: result.resultsGoalHistory }
                : e
            )
          : prev
      );
      queryClient.invalidateQueries({ queryKey: ["experiments", propertyId] });
      queryClient.invalidateQueries({ queryKey: ["experiment-metrics", propertyId, experimentId] });
      queryClient.invalidateQueries({ queryKey: ["experiment-goal-metrics", propertyId, experimentId] });
      logResultsGoalChanged(
        { propertyId },
        {
          experimentId,
          goalType: resultsGoal?.type ?? "launch",
          clickPreset: resultsGoal?.clicks?.preset ?? null,
          clickTargets: resultsGoal?.clicks?.targets.length ?? null,
        }
      );
    },
    onError: (error, { experimentId }) => {
      if (getHttpStatus(error) === 409) {
        logResultsGoalConflict({ propertyId }, { experimentId });
        return;
      }
      reportExperimentFailure("change_results_goal", error, { propertyId }, { experimentId });
    },
  });
}

/**
 * Hook to fetch pages for experiment setup
 */
export function useExperimentPages(options?: { propertyId?: string }) {
  const { token } = useAuth();
  const propertyId = usePropertyId(options?.propertyId);

  return useQuery({
    queryKey: ["experiment-pages", propertyId],
    queryFn: async () => {
      if (!token || !propertyId) throw new Error("Missing auth or propertyId");
      const response = await getExperimentPages(token, propertyId);
      // API returns array directly, normalize to { pages: [...] }
      return Array.isArray(response) ? { pages: response } : response;
    },
    enabled: !!token && !!propertyId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to create a new experiment
 */
export function useCreateExperiment(options?: { propertyId?: string }) {
  const { token } = useAuth();
  const propertyId = usePropertyId(options?.propertyId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      experimentName: string;
      variants: ExperimentVariant[];
      testGoal: string;
      isUserLevel?: boolean;
      geoRestriction?: GeoRestriction;
      audienceRestriction?: AudienceRestriction;
      finishPageUrl?: string;
      testType?: string;
      notes?: string; // Experiment-level notes
      trafficDistribution?: number[]; // Array of percentages for each variant
      draftId?: string; // For component tests
      autoStopDurationDays?: number | null;
    }) => {
      if (!token || !propertyId) throw new Error("Missing auth or propertyId");
      return createExperiment(
        propertyId,
        data.variants.map((v) => ({
          id: v.id,
          url: v.url,
          notes: v.notes || "",
          label: v.label,
          isControl: v.isControl,
          screenshots: v.screenshots || [],
          instructions: v.instructions || [], // Include instructions for component tests
        })),
        data.experimentName,
        data.isUserLevel ?? false,
        token,
        data.testGoal,
        data.geoRestriction,
        data.audienceRestriction,
        data.finishPageUrl,
        data.testType,
        data.notes,
        data.trafficDistribution,
        data.draftId,
        data.autoStopDurationDays
      );
    },
    onSuccess: (_, variables) => {
      logExperimentEvent("experiment_created", { propertyId }, {
        experimentName: variables.experimentName,
        experimentType: variables.testType,
      });
      queryClient.invalidateQueries({ queryKey: ["experiments", propertyId] });
      queryClient.invalidateQueries({
        queryKey: ["experiment-traffic", propertyId],
      });
      queryClient.invalidateQueries({ queryKey: ["account-experiment-usage"] });
      // A new experiment consumes an active-test slot, so plan usage
      // meters/banners need to reflect the new count immediately.
      queryClient.invalidateQueries({ queryKey: ["plan-usage"] });
    },
  });
}

/**
 * Hook to stop/pause an experiment
 */
export function useStopExperiment(options?: { propertyId?: string }) {
  const { token } = useAuth();
  const propertyId = usePropertyId(options?.propertyId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (experimentId: string) => {
      if (!token || !propertyId) throw new Error("Missing auth or propertyId");
      return stopExperiment(propertyId, experimentId, token);
    },
    onSuccess: (_, experimentId) => {
      logExperimentEvent("experiment_stopped", { propertyId }, { experimentId });
      queryClient.invalidateQueries({ queryKey: ["experiments", propertyId] });
      queryClient.invalidateQueries({
        queryKey: ["experiment-metrics", propertyId, experimentId],
      });
      // Stopping frees an active-test slot.
      queryClient.invalidateQueries({ queryKey: ["plan-usage"] });
    },
    onError: (error, experimentId) => {
      reportExperimentFailure("stop", error, { propertyId }, { experimentId });
    },
  });
}

/**
 * Hook to resume a paused experiment
 */
export function useResumeExperiment(options?: { propertyId?: string }) {
  const { token } = useAuth();
  const propertyId = usePropertyId(options?.propertyId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (experimentId: string) => {
      if (!token || !propertyId) throw new Error("Missing auth or propertyId");
      return resumeExperiment(propertyId, experimentId, token);
    },
    onSuccess: (_, experimentId) => {
      logExperimentEvent("experiment_resumed", { propertyId }, { experimentId });
      queryClient.invalidateQueries({ queryKey: ["experiments", propertyId] });
      queryClient.invalidateQueries({
        queryKey: ["experiment-metrics", propertyId, experimentId],
      });
      // Resuming consumes an active-test slot again.
      queryClient.invalidateQueries({ queryKey: ["plan-usage"] });
    },
    onError: (error, experimentId) => {
      reportExperimentFailure("resume", error, { propertyId }, { experimentId });
    },
  });
}

/**
 * Hook to archive an experiment
 */
export function useArchiveExperiment(options?: { propertyId?: string }) {
  const { token } = useAuth();
  const propertyId = usePropertyId(options?.propertyId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (experimentId: string) => {
      if (!token || !propertyId) throw new Error("Missing auth or propertyId");
      return archiveExperiment(propertyId, experimentId, token);
    },
    onSuccess: (_, experimentId) => {
      logExperimentEvent("experiment_archived", { propertyId }, { experimentId });
      queryClient.invalidateQueries({ queryKey: ["experiments", propertyId] });
      // Archiving an active test frees its slot.
      queryClient.invalidateQueries({ queryKey: ["plan-usage"] });
    },
    onError: (error, experimentId) => {
      reportExperimentFailure("archive", error, { propertyId }, { experimentId });
    },
  });
}

type CallExperimentInput = {
  decision: ExperimentDecisionPayload
  /** Local log enrichment (name, goal, etc.) */
  log?: Omit<ExperimentLogEntry, "experimentId" | "propertyId" | "decidedAt" | "action" | "winnerVariantId" | "liftPercent" | "significancePercent" | "rationale">
  /** Also stop the experiment after recording the decision. */
  stopAfter?: boolean
}

/**
 * Record a call (decision) for an experiment. Persists to the learning log
 * and best-effort hits the backend decision endpoint. Optionally stops the test.
 */
export function useCallExperiment(options?: { propertyId?: string }) {
  const { token } = useAuth();
  const propertyId = usePropertyId(options?.propertyId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ decision, log, stopAfter = true }: CallExperimentInput) => {
      if (!token || !propertyId) throw new Error("Missing auth or propertyId");
      const remote = await recordExperimentDecision(propertyId, decision, token);
      if (stopAfter) {
        try {
          await stopExperiment(propertyId, decision.experimentId, token);
        } catch {
          /* stop may already have been applied by the decision endpoint */
        }
      }
      appendExperimentLog({
        experimentId: decision.experimentId,
        propertyId,
        experimentName: log?.experimentName || decision.experimentId,
        testGoal: log?.testGoal,
        testType: log?.testType,
        pageUrl: log?.pageUrl,
        winnerVariantId: decision.winnerVariantId,
        winnerLabel: log?.winnerLabel,
        action: decision.action,
        liftPercent: decision.liftPercent,
        significancePercent: decision.significancePercent,
        rationale: decision.rationale,
        hypothesis: log?.hypothesis,
        decidedAt: new Date().toISOString(),
        domain: log?.domain,
      });
      return remote;
    },
    onSuccess: (_, vars) => {
      logExperimentEvent("decision_called", { propertyId, domain: vars.log?.domain }, {
        experimentId: vars.decision.experimentId,
        experimentName: vars.log?.experimentName,
        experimentType: vars.log?.testType,
        variantId: vars.decision.winnerVariantId,
      });
      queryClient.invalidateQueries({ queryKey: ["experiments", propertyId] });
      queryClient.invalidateQueries({
        queryKey: ["experiment-metrics", propertyId, vars.decision.experimentId],
      });
    },
    onError: (error, vars) => {
      reportExperimentFailure("call_decision", error, { propertyId, domain: vars.log?.domain }, {
        experimentId: vars.decision.experimentId,
        experimentName: vars.log?.experimentName,
      });
    },
  });
}

/**
 * Ship a winning no-code variant to 100% traffic, record the decision, and stop.
 */
export function useShipWinner(options?: { propertyId?: string }) {
  const { token } = useAuth();
  const propertyId = usePropertyId(options?.propertyId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      experimentId,
      winnerVariantId,
      log,
      liftPercent,
      significancePercent,
    }: {
      experimentId: string
      winnerVariantId: string
      liftPercent?: number | null
      significancePercent?: number | null
      log?: CallExperimentInput["log"]
    }) => {
      if (!token || !propertyId) throw new Error("Missing auth or propertyId");
      const promote = await promoteExperimentVariant(
        propertyId,
        experimentId,
        winnerVariantId,
        token,
      );
      const decision: ExperimentDecisionPayload = {
        experimentId,
        winnerVariantId,
        action: "ship",
        liftPercent,
        significancePercent,
        rationale: "Shipped winner to 100% via no-code runtime",
      };
      await recordExperimentDecision(propertyId, decision, token);
      try {
        await stopExperiment(propertyId, experimentId, token);
      } catch {
        /* ignore */
      }
      appendExperimentLog({
        experimentId,
        propertyId,
        experimentName: log?.experimentName || experimentId,
        testGoal: log?.testGoal,
        testType: log?.testType,
        pageUrl: log?.pageUrl,
        winnerVariantId,
        winnerLabel: log?.winnerLabel,
        action: "ship",
        liftPercent,
        significancePercent,
        rationale: decision.rationale,
        hypothesis: log?.hypothesis,
        decidedAt: new Date().toISOString(),
        domain: log?.domain,
      });
      return promote;
    },
    onSuccess: (_, vars) => {
      logExperimentEvent("winner_shipped", { propertyId, domain: vars.log?.domain }, {
        experimentId: vars.experimentId,
        experimentName: vars.log?.experimentName,
        variantId: vars.winnerVariantId,
      });
      queryClient.invalidateQueries({ queryKey: ["experiments", propertyId] });
      queryClient.invalidateQueries({
        queryKey: ["experiment-metrics", propertyId, vars.experimentId],
      });
    },
    onError: (error, vars) => {
      reportExperimentFailure("ship_winner", error, { propertyId, domain: vars.log?.domain }, {
        experimentId: vars.experimentId,
        variantId: vars.winnerVariantId,
      });
    },
  });
}

// Utility functions for experiment calculations.
// Canonical engine: src/lib/utils/abTestStatistics.ts (precise two-proportion Z-test).
// These wrappers keep the existing call sites stable while routing through one engine.

/**
 * Significance % for conversion-rate goals. Uses the shared frequentist engine
 * (two-proportion Z-test with continuous p-value), not the old z-bucket table.
 */
export function calculateStatisticalSignificance(
  controlConversions: number,
  controlSessions: number,
  variantConversions: number,
  variantSessions: number
): number {
  return twoProportionZTest(
    controlConversions,
    controlSessions,
    variantConversions,
    variantSessions
  ).significancePercent;
}

/**
 * Significance % for count goals (the click goal counts every click, so a visitor
 * can add 0, 1 or 12). Rate test with pooled λ and a dispersion factor; see
 * estimateCountDispersion. Below 1 click per visitor the binomial test would
 * treat 0.94 as a 94% share and overstate certainty.
 */
export function calculatePoissonRateSignificance(
  controlEvents: number,
  controlSessions: number,
  variantEvents: number,
  variantSessions: number,
  dispersion: number = 1
): number {
  return twoProportionZTest(
    controlEvents,
    controlSessions,
    variantEvents,
    variantSessions,
    95,
    { metric: "count", dispersion }
  ).significancePercent;
}

/** The significance function for a goal: rate test for counts, two-proportion otherwise. */
export function significanceForGoal(
  testGoal: string | null | undefined,
  dispersion: number = 1
): (cConv: number, cSess: number, vConv: number, vSess: number) => number {
  return isCountGoal(testGoal)
    ? (a, b, c, d) => calculatePoissonRateSignificance(a, b, c, d, dispersion)
    : calculateStatisticalSignificance;
}

export function calculateImprovement(
  controlRate: number,
  variantRate: number
): number {
  if (controlRate === 0) return variantRate > 0 ? 100 : 0;
  return ((variantRate - controlRate) / controlRate) * 100;
}

export function determineWinner(
  variants: ExperimentVariant[],
  minimumSignificance: number = 95,
  testGoal?: string,
  dispersion: number = 1
): { winner: ExperimentVariant | null; significance: number } {
  if (variants.length < 2) return { winner: null, significance: 0 };

  const control = variants.find((v) => v.isControl);
  if (!control) return { winner: null, significance: 0 };

  let bestVariant: ExperimentVariant | null = null;
  let bestSignificance = 0;
  const calcSignificance = significanceForGoal(testGoal, dispersion);

  for (const variant of variants) {
    if (variant.isControl) continue;

    const significance = calcSignificance(
      control.conversions || 0,
      control.sessions || 0,
      variant.conversions || 0,
      variant.sessions || 0
    );

    if (
      significance >= minimumSignificance &&
      (variant.conversionRate || 0) > (control.conversionRate || 0) &&
      significance > bestSignificance
    ) {
      bestVariant = variant;
      bestSignificance = significance;
    }
  }

  return { winner: bestVariant, significance: bestSignificance };
}

// ============================================================================
// AB Test Draft Hooks (No-Code Component Testing)
// ============================================================================

/**
 * Hook to fetch a specific AB test draft
 */
export function useAbTestDraft(draftId: string | null) {
  const { token } = useAuth();

  return useQuery({
    queryKey: ["ab-test-draft", draftId],
    queryFn: async () => {
      if (!token || !draftId) throw new Error("Missing auth or draftId");
      return getAbTestDraft(draftId, token);
    },
    enabled: !!token && !!draftId,
    staleTime: 10 * 1000, // Shorter stale time for active editing
  });
}

/**
 * Hook to update an AB test draft
 */
export function useUpdateAbTestDraft() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      draftId: string;
      variant: string;
      instructions: ComponentInstruction[];
      metadata?: Partial<AbTestDraft["metadata"]>;
    }) => {
      if (!token) throw new Error("Missing auth");
      return updateAbTestDraft(
        data.draftId,
        data.variant,
        data.instructions,
        data.metadata || {},
        token
      );
    },
    onSuccess: (_, variables) => {
      logExperimentEvent("draft_saved", {}, { draftId: variables.draftId });
      queryClient.invalidateQueries({
        queryKey: ["ab-test-draft", variables.draftId],
      });
    },
    onError: (error, variables) => {
      reportExperimentFailure("save_draft", error, {}, { draftId: variables.draftId });
    },
  });
}

/**
 * Hook to generate AI-powered variations for an element
 */
export function useGenerateVariation() {
  const { token } = useAuth();

  return useMutation({
    mutationFn: async (data: {
      elementData: {
        selector: string;
        html: string;
        text?: string;
        styles?: Record<string, string>;
        context?: string;
      };
      variationType: "text" | "css" | "html" | "attributes";
      prompt: string;
    }) => {
      if (!token) throw new Error("Missing auth");
      return generateVariation(
        data.elementData,
        data.variationType,
        data.prompt,
        token
      );
    },
  });
}

